import axios from 'axios';
import { Layout } from 'components/layout';
import { PublicListingView } from 'components/marketplace-listing-view/public-listing';
import { publicRoute } from 'components/with-auth/public-route';
import { format } from 'date-fns';
import { useMarketplacePublicListingDetail } from 'hooks/use-marketplace-public-listing-detail';
import { VisibilityEnum } from 'lib/api/generated';
import { getSiteOrigin } from 'lib/api/public-server-fetch';
import { buildPublicListingPath } from 'lib/listing-utils';
import { NextPage } from 'next';
import { getSession } from 'next-auth/react';
import Head from 'next/head';
import styled from 'styled-components';
import { ErrorMessage, Message } from 'components/ui';
import { MarketplaceListings } from 'components/marketplace';
import { MarketplaceNotificationDialog } from 'components/marketplace-notification-dialog';
import { useDialogToggle } from 'hooks/use-dialog-toggle';
import { prefetchPublicListingsState } from 'lib/api/prefetch-public-listings';
import { cld, DATE_FORMAT, PATHS } from 'settings/config';
import { breakpoints } from 'styles/breakpoints';
import { globalTheme } from 'styles/global-theme';
import { fill } from '@cloudinary/url-gen/actions/resize';

// Sits on the marketplace surface (white, full-width) above the listings grid,
// with horizontal padding matching the grid's ListWrapper so the banner aligns.
const GoneBannerWrapper = styled.div`
  display: flex;
  justify-content: center;
  background-color: ${globalTheme.colors.white};
  padding: ${globalTheme.space[5]} ${globalTheme.space[5]} 0;

  @media screen and (max-width: ${breakpoints.medium}) {
    padding: ${globalTheme.space[1]} ${globalTheme.space[1]} 0;
  }
`;

const GONE_MESSAGES = {
  expired:
    'This listing is no longer active. Browse the marketplace below for similar listings.',
  notFound:
    "We couldn't find that listing, it may have been removed or the link is incorrect. Browse the marketplace below for similar listings.",
} as const;

const GONE_TITLES = {
  expired: 'Listing no longer active | Agora Livestock',
  notFound: 'Listing not found | Agora Livestock',
} as const;

// Gone listing (API 410 / unknown id): keep the URL, status code and noindex so
// crawlers drop the stale URL, but render the full marketplace below a notice
// banner so the visitor isn't left at a dead end. Lives in its own component so
// its hooks stay clear of the page component's early returns.
const ListingGoneView = ({ reason }: { reason: 'expired' | 'notFound' }) => {
  const [
    notificationDialogOpen,
    openNotificationDialog,
    closeNotificationDialog,
  ] = useDialogToggle(false);

  return (
    <Layout
      meta={{
        title: GONE_TITLES[reason],
        removeDefaultTitleSuffix: true,
      }}
    >
      <Head>
        <meta name='robots' content='noindex, nofollow' />
      </Head>
      <GoneBannerWrapper>
        <Message backgroundColor='lightBlue' color='blue'>
          {GONE_MESSAGES[reason]}
        </Message>
      </GoneBannerWrapper>
      <MarketplaceListings
        route={PATHS.marketplace.listings}
        openNotificationDialog={openNotificationDialog}
        forceQueryEnabled
      />
      <MarketplaceNotificationDialog
        isOpen={notificationDialogOpen}
        onDismiss={() => {
          closeNotificationDialog();
        }}
      />
    </Layout>
  );
};

const ViewPublicListingPage: NextPage = (props: any) => {
  const { listingDetails, canonicalUrl } = props;
  const agoraLogo = `${process.env.NEXT_PUBLIC_URL}/images/agora_business_card.png`;

  // Backend-generated SEO (metadata.seo). Prefer these values; the template
  // helpers below (getPageTitle/getDescription) remain as fallbacks for any
  // listing without generated SEO.
  const seo = listingDetails?.metadata?.seo;

  const { data: publicListingData, refetch } =
    useMarketplacePublicListingDetail({
      listingId: listingDetails?.id as string,
    });

  const getImageToDisplay = (): string | undefined => {
    if (!listingDetails.media || listingDetails.media.length === 0) {
      return agoraLogo;
    }

    const cloudImages = listingDetails.media.map((media: any) => {
      if (
        media?.resource_type === 'image' ||
        media?.resource_type === 'video'
      ) {
        const cloudImage = cld
          .image(media?.public_id)
          .format('jpg')
          .quality('auto')
          .resize(fill().aspectRatio('3:2').width(1600).height(800));
        const myVideo = cld
          .video(media?.public_id)
          .format('jpg')
          .quality('auto')
          .resize(fill().width(1600).height(800));
        return media.resource_type === 'image'
          ? cloudImage.toURL()
          : myVideo.toURL();
      }
    });

    return cloudImages.shift();
  };

  const getDescription = (): string | undefined => {
    const parts: string[] = [];

    if (listingDetails.quantity_label) {
      parts.push(listingDetails.quantity_label);
    }
    if (listingDetails.sold) {
      parts.push(
        `Filled ${format(new Date(listingDetails?.sold), DATE_FORMAT.listing)}`
      );
    }
    if (listingDetails.prices_label !== '') {
      parts.push(listingDetails.prices_label);
    }
    return parts.join(', ');
  };

  // Fallback <title> / og:title / twitter:title used when the backend hasn't
  // supplied a generated SEO title. Wanted (BUY) listings keep the "Wanted:"
  // prefix; for-sale (SELL) listings get "For Sale" appended. The brand suffix
  // is added separately by withBrandSuffix so it applies to backend SEO titles
  // too.
  const getPageTitle = (): string => {
    const { type, title, sold } = listingDetails;

    let baseTitle: string = title;
    if (type === 'SELL') {
      baseTitle = sold ? `Filled: ${title}` : `${title} For Sale`;
    } else if (type === 'BUY') {
      baseTitle = sold ? `Bought: ${title}` : `Wanted: ${title}`;
    }

    return baseTitle;
  };

  // Appends the marketplace brand suffix to whichever title we render (backend
  // SEO title or the getPageTitle fallback), so the <title>, og:title and
  // twitter:title all end with " | Agora Marketplace | Buy & Sell Livestock".
  const withBrandSuffix = (title: string): string =>
    [title, 'Agora Marketplace', 'Buy & Sell Livestock']
      .filter(Boolean)
      .join(' | ');

  // schema.org structured data so search engines / AI crawlers can read the
  // listing as a product offering. Rendered server-side from `listingDetails`.
  const getStructuredData = (): string => {
    const pageUrl =
      canonicalUrl ||
      `${process.env.NEXT_PUBLIC_URL}${buildPublicListingPath(listingDetails)}`;
    const categoryLabels: { [key: string]: string } = {
      CT: 'Cattle',
      SH: 'Sheep',
      GT: 'Goats',
    };
    // Common Offer fields shared by both the price-less and priced variants.
    const offerBase = {
      priceCurrency: 'AUD',
      availability: listingDetails.sold
        ? 'https://schema.org/SoldOut'
        : 'https://schema.org/InStock',
      url: pageUrl,
      ...(listingDetails.org?.name
        ? {
            seller: {
              '@type': 'Organization',
              name: listingDetails.org.name,
            },
          }
        : {}),
    };

    // schema.org Offer / AggregateOffer. Prices are plain AUD dollar amounts
    // (per head or per kg — a legitimate unit price). We only emit a numeric
    // price when it's unambiguous:
    //  - skip entirely for POA / negotiable-only / empty (keep a price-less Offer)
    //  - a single clean value → `price`
    //  - multiple values of the SAME unit type → AggregateOffer low/high range
    //  - mixed unit types ($/head + $/kg) → no numeric price (combining them
    //    into a range would be meaningless), so fall back to a price-less Offer
    const buildOffers = () => {
      const pd = listingDetails.prices_data;
      const validUntil = listingDetails.expiry
        ? String(listingDetails.expiry).slice(0, 10)
        : undefined;

      if (
        !pd ||
        pd.poa ||
        !Array.isArray(pd.prices) ||
        pd.prices.length === 0
      ) {
        return { '@type': 'Offer', ...offerBase };
      }

      const parsed = pd.prices
        .map((p: any) => ({
          value: Number(String(p?.price).replace(/[^0-9.]/g, '')),
          type: p?.type,
        }))
        .filter((p: any) => Number.isFinite(p.value) && p.value > 0);

      const uniqueTypes = new Set(parsed.map((p: any) => p.type));
      if (parsed.length === 0 || uniqueTypes.size > 1) {
        return { '@type': 'Offer', ...offerBase };
      }

      const values = parsed.map((p: any) => p.value);
      if (values.length === 1) {
        return {
          '@type': 'Offer',
          ...offerBase,
          price: values[0],
          ...(validUntil ? { priceValidUntil: validUntil } : {}),
        };
      }

      return {
        '@type': 'AggregateOffer',
        ...offerBase,
        lowPrice: Math.min(...values),
        highPrice: Math.max(...values),
        offerCount: values.length,
      };
    };

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: listingDetails.title,
      description: getDescription(),
      image: getImageToDisplay(),
      category:
        categoryLabels[listingDetails.product] || listingDetails.product,
      url: pageUrl,
      offers: buildOffers(),
    };
    // Escape `<` to prevent breaking out of the <script> tag.
    return JSON.stringify(structuredData).replace(/</g, '\\u003c');
  };

  if (props.error && !listingDetails) {
    return <ErrorMessage>Listing not found</ErrorMessage>;
  }

  if (props.gone) {
    return <ListingGoneView reason={props.gone} />;
  }

  // Only PUBLIC listings should be indexed. Anything else (PRIVATE, HIDDEN,
  // EXPIRED, ARCHIVED, DELETED, or an unknown/missing value) is reachable by
  // direct URL but is not part of the public marketplace, so we noindex it and
  // drop the canonical / structured data to avoid "please index this" signals.
  const isPublic = listingDetails?.visibility === VisibilityEnum.Public;

  // if (!hydrated) {
  //   // this returns null on first render, so the client and server match
  //   return null;
  // }

  return (
    <Layout
      meta={{
        title: withBrandSuffix(seo?.page_title || getPageTitle()),
        removeDefaultTitleSuffix: true,
      }}
    >
      {listingDetails && (
        <>
          <Head>
            {/* HTML Meta Tags */}
            <meta
              name='description'
              content={seo?.meta_description || getDescription()}
            />
            {!isPublic ? (
              <meta name='robots' content='noindex, nofollow' />
            ) : (
              <>
                {canonicalUrl && <link rel='canonical' href={canonicalUrl} />}

                {/* Structured data for search engines / AI crawlers */}
                <script
                  type='application/ld+json'
                  dangerouslySetInnerHTML={{ __html: getStructuredData() }}
                />
              </>
            )}

            {/* Facebook Meta Tags. Keyed so Next dedupes against the shared
                SEO component (which also emits og:title/description/image) —
                these page-specific values win because this Head renders later. */}
            <meta property='og:url' content={canonicalUrl} key='og:url' />
            <meta property='og:type' content='website' key='og:type' />
            <meta
              property='og:title'
              content={withBrandSuffix(seo?.social?.og_title || getPageTitle())}
              key='og:title'
            />
            <meta
              property='og:description'
              content={seo?.social?.og_description || getDescription()}
              key='og:description'
            />
            <meta
              property='og:image'
              content={getImageToDisplay()}
              key='og:image'
            />
            <meta
              property='og:image:alt'
              content={
                seo?.social?.og_image_alt ||
                listingDetails.title ||
                'Agora Livestock listing'
              }
              key='og:image:alt'
            />
            <meta
              property='og:image:type'
              content='image/jpeg'
              key='og:image:type'
            />

            {/* Twitter Meta Tags */}
            <meta name='twitter:card' content='summary_large_image' />
            <meta property='twitter:domain' content='agoralivestock.com.au' />
            <meta property='twitter:url' content={canonicalUrl} />
            <meta
              name='twitter:title'
              content={withBrandSuffix(
                seo?.social?.twitter_title || getPageTitle()
              )}
            />
            <meta
              name='twitter:description'
              content={seo?.social?.twitter_description || getDescription()}
            />
            <meta name='twitter:image' content={getImageToDisplay()} />
          </Head>
          <PublicListingView
            {...props}
            publicListingData={publicListingData}
            refetch={refetch}
          />
        </>
      )}
    </Layout>
  );
};

export const getServerSideProps = async (context: any) => {
  const { id } = context.query;
  let listingDetails: any = null;
  let listingError = null;
  let listingStatus: number | null = null;
  const session = await getSession(context);

  if (id) {
    // Pass all any x-agora headers through to the API request.
    // Only attach the bearer token when there's a session — anonymous crawlers
    // get the public listing data without it (omitting the header avoids
    // sending a useless `Bearer undefined`).
    const headers: { [key: string]: string } = {};
    if (session?.accessToken) {
      headers.Authorization = `Bearer ${session.accessToken}`;
    }
    for (let key in context.req.headers) {
      if (key.toLowerCase().startsWith('x-agora')) {
        headers[key] = context.req.headers[key];
      }
    }

    // using axios instead of the API client as the API client doesn't work server side.
    await axios({
      method: 'get',
      url: `${process.env.NEXT_PUBLIC_API_BASE_PATH}/v2/marketplace/listing/${id}`,
      headers: headers,
      params: { aa_source: 'ssr' },
    })
      .then((data) => {
        listingDetails = data?.data;
      })

      .catch((error: any) => {
        if (error) {
          listingError = error.message;
          listingStatus = error.response?.status ?? null;
        }
      });
  }

  // Canonical carries the decorative title slug (when we have the listing), so
  // the slugged URL is the one search engines index. Falls back to the bare id
  // when the fetch failed (transient-error branch below).
  const canonicalUrl = `${getSiteOrigin(context.req)}${buildPublicListingPath(
    listingDetails ?? { id }
  )}`;

  if (!listingDetails) {
    // A 5xx or network failure is transient — surface a 503 so crawlers retry
    // rather than dropping a real listing from the index.
    const transient = listingError && (!listingStatus || listingStatus >= 500);
    if (transient) {
      context.res.statusCode = 503;
      return {
        props: {
          error: listingError,
          canonicalUrl,
        },
      };
    }

    // Gone listing: expired (API 410) or genuinely not found (any other
    // non-transient failure, incl. unknown ids). Keep the honest status code +
    // noindex so crawlers drop the URL, but render the marketplace below a
    // notice banner instead of a dead-end message/404 page. Prefetch the
    // listings grid for anonymous visitors (same as /marketplace); the gone
    // response is CDN-cacheable for them too.
    const gone = listingStatus === 410 ? 'expired' : 'notFound';
    context.res.statusCode = listingStatus === 410 ? 410 : 404;

    let dehydratedState: unknown = null;
    if (!session) {
      context.res.setHeader(
        'Cache-Control',
        'public, s-maxage=300, stale-while-revalidate=600'
      );
      ({ dehydratedState } = await prefetchPublicListingsState(context.query));
    }

    return {
      props: {
        gone,
        // SessionProvider (_app) needs the session in pageProps for useSession
        // to resolve during SSR — without it the marketplace grid renders its
        // loader instead of the prefetched listings.
        session,
        ...(dehydratedState ? { dehydratedState } : {}),
      },
    };
  }

  // Cache the anonymous (public) HTML at the CDN so crawler traffic doesn't hit
  // the origin every request — mirrors /marketplace. Authenticated responses and
  // non-public (noindex) listings are never cached: the former are token-scoped,
  // the latter can change visibility and must not be served stale-as-public.
  if (!session && listingDetails.visibility === VisibilityEnum.Public) {
    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=300, stale-while-revalidate=600'
    );
  }

  // return the listing details
  return {
    props: {
      listingDetails: listingDetails,
      listingError: null,
      canonicalUrl,
    },
  };
};

export default publicRoute(ViewPublicListingPage, {
  marketplace: true,
});
